import React, { useState, useEffect, useMemo } from 'react';
import { Table, Select, App, Typography, Input } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useAuth } from '../context/AuthContext';
import { listUsers, changeUserRole } from '../services/api';
import { useThemeStyles } from '../context/ThemeContext';
import PageHeader from '../components/PageHeader';
import { pageShellStyle } from '../theme';

const { Text } = Typography;
const normalizeRole = (role) => (String(role || '').toLowerCase() === 'superadmin' ? 'super_admin' : String(role || '').toLowerCase());

const PAGE_SIZE = 5;

function UserManagement() {
  const { message } = App.useApp();
  const { token, isSuperAdmin } = useAuth();
  const themeStyles = useThemeStyles();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [usernameQuery, setUsernameQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchUsers = async () => {
    if (!token || !isSuperAdmin) return;
    setLoading(true);
    try {
      const res = await listUsers(token);
      const data = Array.isArray(res.data) ? res.data : [];
      setUsers(data.map((u) => ({ ...u, role: normalizeRole(u.role) })));
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, isSuperAdmin]);

  useEffect(() => {
    setCurrentPage(1);
  }, [usernameQuery]);

  const filteredUsers = useMemo(() => {
    const q = usernameQuery.trim().toLowerCase();
    return users.filter((u) => {
      if (q && !String(u.username || '').toLowerCase().includes(q)) return false;
      return true;
    });
  }, [users, usernameQuery]);

  const pagedUsers = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredUsers.slice(start, start + PAGE_SIZE);
  }, [filteredUsers, currentPage]);

  const handleRoleChange = async (userId, newRole) => {
    try {
      await changeUserRole(token, userId, newRole);
      message.success('Role updated');
      fetchUsers();
    } catch (e) {
      message.error(e.response?.data?.detail || 'Failed to update role');
    }
  };

  const headerCellProps = () => ({
    style: {
      background: themeStyles.userTableHeaderBg,
      fontWeight: 600,
      borderBottom: `1px solid ${themeStyles.userTableHeaderBorder}`,
      color: themeStyles.textPrimary,
    },
  });

  const columns = [
    {
      title: 'Username',
      dataIndex: 'username',
      key: 'username',
      onHeaderCell: headerCellProps,
      render: (t) => (
        <Text strong style={{ color: themeStyles.textPrimary, fontSize: 13 }}>
          {t}
        </Text>
      ),
    },
    {
      title: 'Role',
      key: 'role',
      width: 170,
      onHeaderCell: headerCellProps,
      render: (_, record) => {
        const isSuperAdminRow = record.role === 'super_admin';
        if (isSuperAdminRow) {
          return <Text type="secondary" style={{ fontSize: 13 }}>Locked</Text>;
        }
        return (
          <Select
            className="user-management-role-select"
            size="small"
            value={record.role}
            style={{ width: 160, fontSize: 13 }}
            onChange={(val) => handleRoleChange(record._id, val)}
            options={[
              { label: 'User', value: 'user' },
              { label: 'Admin', value: 'admin' },
            ]}
          />
        );
      },
    },
  ];

  if (!isSuperAdmin) {
    return (
      <div style={{ ...pageShellStyle, background: themeStyles.contentBg }}>
        <Text style={{ color: themeStyles.textSecondary }}>Super admin access required.</Text>
      </div>
    );
  }

  return (
    <div style={{ ...pageShellStyle, background: themeStyles.contentBg }}>
      <PageHeader title="User Management" />

      <div style={{ marginBottom: 16, width: '100%', maxWidth: 560 }}>
        <Input
          allowClear
          placeholder="Search by username"
          prefix={<SearchOutlined style={{ color: themeStyles.textTertiary }} />}
          value={usernameQuery}
          onChange={(e) => setUsernameQuery(e.target.value)}
          style={{ width: '100%' }}
        />
      </div>

      <div className="user-management-table-wrap">
        <Table
          className="user-management-table"
          dataSource={pagedUsers}
          columns={columns}
          rowKey="_id"
          loading={loading}
          size="small"
          tableLayout="fixed"
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: PAGE_SIZE,
            total: filteredUsers.length,
            showSizeChanger: false,
            size: 'small',
            className: 'user-management-pagination',
            onChange: (page) => setCurrentPage(page),
            hideOnSinglePage: false,
          }}
          onRow={(record, rowIndex) => {
            const globalIndex = (currentPage - 1) * PAGE_SIZE + rowIndex;
            const baseBg = globalIndex % 2 === 0
              ? themeStyles.userTableZebraA
              : themeStyles.userTableZebraB;
            return {
              style: { background: baseBg },
              onMouseEnter: (e) => {
                e.currentTarget.style.background = themeStyles.userTableRowHoverBg;
              },
              onMouseLeave: (e) => {
                e.currentTarget.style.background = baseBg;
              },
            };
          }}
        />
      </div>
    </div>
  );
}

export default UserManagement;
